/**
 * Role-based access helper for InsightQore.
 *
 * Roles are identified by their canonical NAME (with alias fallback), not by
 * numeric database ID. Numeric IDs are surrogate keys that are not portable
 * across environments — a database restore or reseed changes them and silently
 * breaks id-based checks. Role names are authored and preserved across
 * restores, so name matching is environment-agnostic.
 *
 * Legacy callers that still pass `{ id: <number> }` continue to work via a
 * fallback branch, which emits a `console.warn` in non-production builds so
 * remaining call sites can be migrated.
 */

/**
 * Canonical InsightQore role catalog.
 *
 * Each entry has:
 *   - `name`:    canonical role name as it should appear in the database
 *   - `aliases`: legacy or variant names that also count as a match
 *
 * When new roles are added, extend this catalog. When a role gets renamed in
 * the database, add the new name here and keep the old one in `aliases` until
 * every environment has been migrated.
 */
export const INSIGHT_QORE_ROLES = {
  ADMIN: {
    name: 'InsightQore - Admin',
    aliases: ['InsightQore Admin'], // legacy no-hyphen variant (e.g. UAT id 137)
  },
  TEAM_MEMBER: {
    name: 'InsightQore - Team Member',
    aliases: [],
  },
  EXECUTIVE: {
    name: 'InsightQore - Executive',
    aliases: [],
  },
  ACCOUNT_ASSISTANT: {
    name: 'InsightQore - Account Assistant',
    aliases: [],
  },
  CRO: {
    name: 'InsightQore - CRO',
    aliases: [],
  },
  SALES_ACCOUNT_EXECUTIVE: {
    name: 'InsightQore - Sales Account Executive',
    aliases: [],
  },
  KEY_ACCOUNT_EXECUTIVE: {
    name: 'InsightQore - Key Account Executive',
    aliases: [],
  },
  CUSTOMER_SUCCESS_EXECUTIVE: {
    name: 'InsightQore - Customer Success Executive',
    aliases: [],
  },
  MTR_VIEW: {
    name: 'InsightQore - MTR View',
    aliases: [],
  },
  CV_VIEW: {
    name: 'InsightQore - CV View',
    aliases: [],
  },
  MTR_EXPORT: {
    name: 'InsightQore - MTR Export',
    aliases: [],
  },
  CV_EXPORT: {
    name: 'InsightQore - CV Export',
    aliases: [],
  },
  SUPERVISOR: {
    name: 'InsightQore - Supervisor',
    aliases: [],
  },
  COMMISSION_EDIT: {
    name: 'InsightQore - Commission Edit',
    aliases: ['InsightQore - Surplus Commission Edit'], // legacy verbose variant
  },
  MANAGER: {
    name: 'InsightQore - Manager',
    aliases: [],
  },
  CHANNEL_LEADER: {
    name: 'InsightQore - Channel Leader',
    aliases: [],
  },
  RSA_CHANNEL_LEADER: {
    name: 'InsightQore - RSA Channel Leader',
    aliases: [],
  },
  FNI_CHANNEL_LEADER: {
    name: 'InsightQore - FNI Channel Leader',
    aliases: [],
  },
  DITTO_MANAGER: {
    name: 'InsightQore - Ditto Manager',
    aliases: [],
  },
  FINTIV_MANAGER: {
    name: 'InsightQore - Fintiv Manager',
    aliases: [],
  },
  ONRAMP_MANAGER: {
    name: 'InsightQore - OnRamp Manager',
    aliases: [],
  },
  // Actuarial Raw Data Export. RUN implies VIEW, but the two are independent
  // grants in the role store, so callers must check VIEW for read paths and
  // RUN for starting an export — holding RUN alone must not unlock the module.
  RAW_DATA_EXPORT_VIEW: {
    name: 'InsightQore - Raw Data Export View',
    aliases: [],
  },
  RAW_DATA_EXPORT_RUN: {
    name: 'InsightQore - Raw Data Export Run',
    aliases: [],
  },
  // Claim Fraud. VIEW covers the dashboard and queue; INVESTIGATE additionally
  // opens claim detail, which exposes VIN, member name and policy number, so
  // the two are separate grants rather than one role.
  CLAIM_FRAUD_VIEW: {
    name: 'InsightQore - Claim Fraud View',
    aliases: [],
  },
  CLAIM_FRAUD_INVESTIGATE: {
    name: 'InsightQore - Claim Fraud Investigate',
    aliases: [],
  },
  // Out-of-network cost estimate from in-network average rates.
  OON_CALCULATOR_VIEW: {
    name: 'InsightQore - OON Calculator View',
    aliases: [],
  },
};

/**
 * Backwards-compatible shim.
 *
 * The pre-existing signature `getRolesByEnv().INSIGHT_QORE.<KEY>` is preserved
 * so the ~28 existing call sites don't have to change. There is no longer any
 * environment branching: the same name-based catalog is returned everywhere.
 */
export const getRolesByEnv = () => ({ INSIGHT_QORE: INSIGHT_QORE_ROLES });

/**
 * Normalize a role name for comparison: trim, collapse internal whitespace,
 * lower-case. This tolerates minor formatting drift ("  InsightQore  Admin"
 * vs "InsightQore Admin") without weakening the match to a substring.
 */
const normalizeRoleName = (value) =>
  (value == null ? '' : String(value)).trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * True iff `userRoleName` matches `roleSpec.name` or any of its aliases.
 */
const matchesRoleSpec = (userRoleName, roleSpec) => {
  const candidate = normalizeRoleName(userRoleName);
  if (!candidate) return false;
  if (candidate === normalizeRoleName(roleSpec.name)) return true;
  const aliases = Array.isArray(roleSpec.aliases) ? roleSpec.aliases : [];
  return aliases.some((alias) => candidate === normalizeRoleName(alias));
};

/**
 * hasAccess(userDefinedRoles, role) -> boolean
 *
 * Preferred: `role = { name, aliases? }` (name-based match).
 * Legacy:    `role = { id }` (numeric id match; emits a dev-mode warning so
 *            remaining call sites can be migrated).
 */
const hasAccess = (userDefinedRoles, role) => {
  if (!Array.isArray(userDefinedRoles) || userDefinedRoles.length === 0 || !role) {
    return false;
  }

  if (role.name) {
    return userDefinedRoles.some((userRole) => matchesRoleSpec(userRole && userRole.name, role));
  }

  if (role.id != null) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[hasAccess] Legacy id-only role spec detected. Migrate this caller to the name-based catalog in src/utils/hasAccess.js.',
        role,
      );
    }
    return userDefinedRoles.some((userRole) => userRole && userRole.id === role.id);
  }

  return false;
};

export default hasAccess;
